import { type ActorDto, TalkingHeadActorEventIdn } from '@newo/api-client';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import useAppStore from '@/store/app';
import {
	type InitSuccessCb,
	useActorSendEventMutation,
	useInitialize
} from '@/utils/api.ts';

const useStepperApp = (actor?: ActorDto) => {
	const isAppInitialized = useRef(false);
	const isResetProcessApplied = useRef(false);
	const initializedActorExternalId = useRef<string | null>(null);
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const queryConfig = searchParams.get('config');
	const resetAppState = useAppStore((state) => state.resetState);
	const { mutateAsync: sendActorEvent } = useActorSendEventMutation();

	const handleSuccess = useCallback(
		({ connectorIdn, externalId, clientSecret }: InitSuccessCb) => {
			if (!clientSecret || !externalId || !connectorIdn) {
				return;
			}

			agentTrainingRealtime.init(
				{
					clientSecret,
					externalId,
					connectorIdn
				},
				{
					onRealtimeConnected: () => {
						if (actor?.external_id && externalId !== actor.external_id) {
							return;
						}

						sendActorEvent({
							client_secret: clientSecret,
							connector_idn: connectorIdn,
							external_id: externalId,
							event_idn: TalkingHeadActorEventIdn.AgentRequest,
							arguments: []
						});
					}
				}
			);
		},
		[actor?.external_id, sendActorEvent]
	);

	const { mutateAsync: initStepperApp } = useInitialize(true, actor);

	useEffect(() => {
		if (!queryConfig || isResetProcessApplied.current) {
			return;
		}

		try {
			const parsed = JSON.parse(atob(queryConfig));

			if (!parsed?.resetProcess) {
				return;
			}

			isResetProcessApplied.current = true;
			resetAppState();

			const nextParams = new URLSearchParams(location.search);
			nextParams.delete('config');
			setSearchParams(nextParams, { replace: true });
		} catch {
			console.warn('Invalid config query param');
		}
	}, [location.search, queryConfig, resetAppState, setSearchParams]);

	useEffect(() => {
		const actorExternalId = actor?.external_id ?? null;

		if (
			isAppInitialized.current &&
			initializedActorExternalId.current === actorExternalId
		) {
			return;
		}

		isAppInitialized.current = true;
		initializedActorExternalId.current = actorExternalId;

		initStepperApp(undefined, {
			onSuccess: handleSuccess
		});
	}, [actor?.external_id, initStepperApp, handleSuccess]);
};

export default useStepperApp;
